import { useTranslate } from '@refinedev/core';
import { App as AntdApp, Button, Input, InputNumber, Space, Typography } from 'antd';
import { useState } from 'react';
import { SectionDrawer, ToolDatePicker } from '../../components/ToolSection';
import { useRefreshClientPage } from '../clients/refreshClientPage';
import {
  emptyPlanDraft,
  planDraftReady,
  planInput,
  type LadderRow,
  type PlanDraft,
} from './model';
import { useActivateCommand } from './useActivateCommand';

/**
 * E41 · "Plan an activity" with the client (PlanActivity, in session) as a side drawer opened from the Plans card (design
 * PORTAL-SECTIONS-2026-10-22 §2.5 (d)1). The client's ladder rungs are chips: one fills "What" and links the plan to that activity
 * (`valueActivityId`); editing "What" afterwards clears the link. Save is never disabled — a missing field is a lasting sentence.
 */
type Props = {
  clientId: string;
  rungs: LadderRow['rungs'];
  onClose: () => void;
  /** After a saved plan: the cards jump to the plan's week. */
  onSaved: (plannedFor: Date) => void;
};

export const BaPlanDrawer = ({ clientId, rungs, onClose, onSaved }: Props) => {
  const t = useTranslate() as unknown as (k: string, o?: Record<string, unknown>) => string;
  const { message } = AntdApp.useApp();
  const refreshClientPage = useRefreshClientPage();
  const { run, isLoading } = useActivateCommand();
  const [draft, setDraft] = useState<PlanDraft>(emptyPlanDraft());
  const [error, setError] = useState<string | null>(null);
  const patch = (p: Partial<PlanDraft>) => {
    setError(null);
    setDraft((d) => ({ ...d, ...p }));
  };

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const onSave = async () => {
    if (!planDraftReady(draft)) {
      setError(t('ba.hub.plan.required'));
      return;
    }
    const res = await run(
      'planActivityInSession',
      planInput(draft, clientId) as unknown as Record<string, unknown>,
      t('ba.hub.plan.error'),
    );
    if (res.ok) {
      message.success(t('ba.hub.plan.saved'));
      refreshClientPage('assign');
      const when = draft.plannedFor as Date;
      onClose();
      onSaved(when);
    } else {
      setError(res.message);
    }
  };

  return (
    <SectionDrawer
      open
      title={t('ba.hub.plan.title')}
      testId="activate-hub-plan"
      saveTestId="activate-hub-plan-submit"
      context={
        <Space direction="vertical" size={8} style={{ width: '100%' }}>
          <Typography.Text type="secondary">{t('ba.hub.plan.intro')}</Typography.Text>
          {rungs.length ? (
            <div>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {t('ba.hub.plan.ladder')}
              </Typography.Text>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 4 }}>
                {[...rungs].reverse().map((r) => (
                  <Button
                    key={r.valueActivityId}
                    size="small"
                    type={draft.valueActivityId === r.valueActivityId ? 'primary' : 'default'}
                    onClick={() => patch({ title: r.title, valueActivityId: r.valueActivityId })}
                    data-testid={`activate-hub-plan-rung-${r.rank}`}
                  >
                    {r.rank}. {r.title}
                  </Button>
                ))}
              </div>
            </div>
          ) : null}
        </Space>
      }
      saveLabel={t('ba.hub.plan.submit')}
      saving={isLoading}
      error={error}
      dirty={JSON.stringify(draft) !== JSON.stringify(emptyPlanDraft())}
      onSave={() => {
        onSave().catch(() => undefined);
      }}
      onClose={onClose}
    >
      <Space direction="vertical" style={{ width: '100%' }}>
        <Input
          addonBefore={t('ba.plan.what')}
          data-testid="activate-hub-plan-title"
          maxLength={300}
          onChange={(e) => patch({ title: e.target.value, valueActivityId: null })}
          value={draft.title}
        />
        <ToolDatePicker
          showTime={{ minuteStep: 5 }}
          disabledDate={(d) => d.getTime() < today.getTime()}
          testId="activate-hub-plan-when"
          placeholder={t('ba.hub.plan.when')}
          onChange={(v) => patch({ plannedFor: v })}
          value={draft.plannedFor}
        />
        <Space wrap>
          <InputNumber
            min={0}
            max={10}
            precision={0}
            addonBefore={t('ba.hub.plan.expectedEnjoyment')}
            data-testid="activate-hub-plan-enjoyment"
            onChange={(v) => patch({ predictedEnjoyment: v === null ? null : Number(v) })}
            value={draft.predictedEnjoyment}
          />
          <InputNumber
            min={0}
            max={10}
            precision={0}
            addonBefore={t('ba.hub.plan.expectedImportance')}
            data-testid="activate-hub-plan-importance"
            onChange={(v) => patch({ predictedImportance: v === null ? null : Number(v) })}
            value={draft.predictedImportance}
          />
          <InputNumber
            min={5}
            max={240}
            precision={0}
            addonBefore={t('ba.hub.plan.durationLabel')}
            data-testid="activate-hub-plan-duration"
            onChange={(v) => patch({ durationMinutes: v === null ? null : Number(v) })}
            value={draft.durationMinutes}
          />
        </Space>
      </Space>
    </SectionDrawer>
  );
};
